import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import offer from '../offer.json';
import { Actions_, Builder_, Dropdown_, FormBox, FormBoxSelection, FormBoxTitle, Grey_, Input_, Main_, Main_Plain, Toggle_ } from 'monica-alexandria';
import { Checkbox_Extra } from '../../../../components/Checkbox_Extra';
import { search_clients } from '../../../../services/api/Clients';
import { get_offer_eligibility } from '../../../../services/api/Offers';
import {
    initial_offer_selected_types,
    offer_type_is_available,
    request_client_should_reset_on_type_change
} from '../utils/offer_creation_rules';

const NewOfferTypes = styled.div`
    display: grid;
    gap: 2rem;
`;

const NewOfferTypesGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
`;

const NewOfferTypesToggle = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
`;

const NewOfferLockedSelection = styled(FormBoxSelection)`
    display: grid;
    gap: 0.4rem;

    p {
        word-break: break-word;
        white-space: pre-wrap;
    }
`;

const RequestClientResults = styled.div`
    display: grid;
    gap: 0.8rem;
`;

const RequestClientOption = styled.button`
    border: 0.1rem solid ${p => p.theme.low};
    border-radius: var(--smallRadius);
    background: ${p => p.theme.background};
    color: ${p => p.theme.color};
    cursor: pointer;
    display: grid;
    gap: 0.4rem;
    padding: var(--smallPads);
    text-align: left;

    &:hover{
        border-color: var(--main);
    }
`;

const RequestClientLockedDetails = styled.div`
    display: grid;
    gap: 0.8rem;
`;

const RequestClientLockedRow = styled.div`
    display: grid;
    grid-template-columns: 14rem 1fr;
    gap: 1rem;
    padding: 0.8rem 0;
    border-bottom: 0.1rem solid ${p => p.theme.low};

    &:last-child{
        border-bottom: none;
    }
`;

const selectable_type_names = ['marketing', 'graphic', 'website_creation', 'website_maintenance'];
const read_type_title = (typeName = '') => {
    if (typeName === 'sass') {
        return 'SASS';
    }

    if (typeName === 'website_creation') {
        return 'Website creation';
    }

    if (typeName === 'website_maintenance') {
        return 'Website maintenance';
    }

    if (typeName === 'website') {
        return 'Website';
    }

    if (typeName === 'marketing') {
        return 'Personalised ads';
    }

    if (typeName === 'graphic' || typeName === 'creative') {
        return 'One-offs';
    }

    return typeName;
};

const get_initial_types = (typesData, requestOfferMode = false) => {
    return initial_offer_selected_types(typesData, requestOfferMode, offer.type);
};

const get_initial_offer_code = (typesData) => {
    return `${typesData?.offerCode || ''}`.trim();
};

const get_initial_client_vat_number = (typesData) => {
    if (typesData?.clientVatNumber?.trim().length) {
        return typesData.clientVatNumber;
    }

    return '';
};

const get_initial_invoice_enabled = (typesData = {}) => {
    if (typesData?.invoiceEnabled === false) {
        return false;
    }

    return true;
};

const get_initial_emails = (typesData) => {
    if (Array.isArray(typesData?.clientEmails)) {
        return typesData.clientEmails;
    }

    return Array.isArray(offer.client_emails) ? offer.client_emails : [];
};

const get_initial_category = (typesData) => {
    const categoryValue = `${typesData?.sourceClientCategory || typesData?.category || typesData?.Category || typesData?.industry || ''}`.trim();

    if (categoryValue.length && categoryValue.toLowerCase() !== 'general') {
        return categoryValue;
    }

    return '';
};

const get_initial_subcategory = (typesData = {}) => {
    return `${typesData?.sourceClientSubcategory || typesData?.subcategory || typesData?.Subcategory || ''}`.trim();
};

const get_initial_client_name = (typesData = {}) => {
    return `${typesData?.clientName || ''}`.trim();
};

const get_initial_client_headquarters = (typesData = {}) => {
    return `${typesData?.clientHeadquarters || ''}`.trim();
};

const get_initial_offer_language = (typesData = {}) => {
    const normalizedLanguage = `${typesData?.offerLanguage || ''}`.trim().toLowerCase();

    if (normalizedLanguage === 'en' || normalizedLanguage === 'el') {
        return normalizedLanguage;
    }

    return 'el';
};

const get_preoffer_enabled_value = (typesData = {}) => {
    if (typesData?.preofferEnabled === true) {
        return true;
    }

    const requestedAt = `${typesData?.preofferRequestedAt || ''}`.trim();
    const normalizedTurn = `${typesData?.preofferTurn || ''}`.trim().toLowerCase();
    const hasLegacyTurn = normalizedTurn === 'team_lead'
        || normalizedTurn === 'teamleader'
        || normalizedTurn === 'team_leader'
        || normalizedTurn === 'teamlead'
        || normalizedTurn === 'coordinator'
        || normalizedTurn === 'done';
    const hasLegacyOwner = Boolean(typesData?.preofferAssignedTeamLead || typesData?.preofferRequestedBy);

    return requestedAt.length > 0 || hasLegacyTurn || hasLegacyOwner;
};

const read_normalized_text = (value) => {
    if (value && typeof value === 'object') {
        return read_normalized_text(
            value.label
            || value.name
            || value.title
            || value.address
            || value.formattedAddress
            || value.fullAddress
            || value.location
            || value.city
            || value.value
        );
    }

    return `${value || ''}`.trim();
};

const read_normalized_key = (value) => {
    return read_normalized_text(value).toLowerCase();
};

const read_prefill_source_is_niffler = (typesData = {}) => {
    const sourceSystem = read_normalized_key(typesData?.sourceSystem);

    return sourceSystem === 'niffler' || sourceSystem === 'niffler-prospect';
};

const read_locked_text = (value = '', fallback = '-') => {
    return `${value || ''}`.trim() || fallback;
};

const read_locked_list_text = (values = [], fallback = '-') => {
    return (values || []).map((value) => `${value || ''}`.trim()).filter(Boolean).join(', ') || fallback;
};

const read_client_brand_name = (client = {}) => {
    return read_normalized_text(client?.brandName || client?.BrandName || client?.brand || client?.name);
};

const read_client_dif_code = (client = {}) => {
    return read_normalized_text(client?.difCode || client?.DIF || client?.dif || client?.code);
};

const read_client_id = (client = {}) => {
    return read_normalized_text(client?.clientId || client?.mongoId || client?._id || client?.id);
};

const read_client_category = (client = {}) => {
    return read_normalized_text(client?.Category || client?.category || client?.businessType || client?.business_type || client?.industry);
};

const read_client_subcategory = (client = {}) => {
    return read_normalized_text(client?.Subcategory || client?.subcategory || client?.subCategory || client?.sector);
};

const read_client_vat = (client = {}) => {
    return read_normalized_text(client?.VAT || client?.vat || client?.Vat || client?.vatNumber || client?.VATNumber);
};

const read_client_location = (client = {}) => {
    return read_normalized_text(client?.location?.address || client?.location || client?.Location || client?.headquarters || client?.Headquarters || client?.address || client?.Address);
};

const read_client_contact_name = (client = {}) => {
    return read_normalized_text(
        client?.contactName
        || client?.ContactName
        || client?.ownerFullName
        || client?.fullName
        || [
            client?.firstname || client?.firstName || client?.ownerFirstname || client?.ownerFirstName,
            client?.lastname || client?.lastName || client?.ownerLastname || client?.ownerLastName
        ].map(read_normalized_text).filter(Boolean).join(' ')
    );
};

const read_client_industry = (client = {}) => {
    return [read_client_category(client), read_client_subcategory(client)].filter(Boolean).join(' / ');
};

const read_client_contact_values = (values = [], objectKeys = []) => {
    return values.flatMap((value) => {
        if (Array.isArray(value)) {
            return read_client_contact_values(value, objectKeys);
        }

        if (value && typeof value === 'object') {
            return read_client_contact_values(objectKeys.map((objectKey) => value?.[objectKey]), objectKeys);
        }

        return `${value || ''}`.split(',');
    }).map((value) => read_normalized_text(value)).filter(Boolean);
};

const read_client_emails = (client = {}) => {
    const values = [
        client?.email,
        client?.clientEmail,
        client?.emailAddress,
        client?.emailAddresses,
        client?.emails
    ].flat().filter(Boolean);

    return read_client_contact_values(values, ['email', 'clientEmail', 'emailAddress', 'address', 'value'])
        .map((value) => value.toLowerCase());
};

const read_client_phones = (client = {}) => {
    const values = [
        client?.phone,
        client?.phones,
        client?.phoneNumber,
        client?.phoneNumbers,
        client?.number,
        client?.numbers,
        client?.mobile,
        client?.mobiles,
        client?.telephone,
        client?.telephones,
        client?.clientPhone,
        client?.clientPhones,
        client?.contactPhone,
        client?.contactPhones,
        client?.phoneList
    ].flat().filter(Boolean);

    return read_client_contact_values(values, ['phone', 'phoneNumber', 'number', 'mobile', 'telephone', 'value']);
};

export const request_client_missing_fields = (client = {}) => {
    const missingFields = [];

    if (!read_client_dif_code(client).length) {
        missingFields.push('DIF code');
    }

    if (!read_client_brand_name(client).length) {
        missingFields.push('brand name');
    }

    if (!read_client_emails(client).length) {
        missingFields.push('emails');
    }

    if (!read_client_phones(client).length) {
        missingFields.push('phones');
    }

    if (!read_client_category(client).length) {
        missingFields.push('category');
    }

    if (!read_client_subcategory(client).length) {
        missingFields.push('subcategory');
    }

    if (!read_client_location(client).length) {
        missingFields.push('location');
    }

    if (!read_client_contact_name(client).length) {
        missingFields.push('contact name');
    }

    return missingFields;
};

export const offer_requires_existing_client = ({
    requestOfferMode = false,
    selectedTypes = [],
    nifflerPrefill = false
} = {}) => {
    return requestOfferMode === true
        || (selectedTypes.includes('marketing') && nifflerPrefill !== true);
};

const read_initial_request_client = (typesData = {}) => {
    if (typesData?.sourceClient && typeof typesData.sourceClient === 'object') {
        return typesData.sourceClient;
    }

    if (!typesData?.sourceClientDifCode) {
        return null;
    }

    return {
        clientId: typesData?.sourceClientId || '',
        brandName: typesData?.sourceClientBrandName || typesData?.clientName || '',
        difCode: typesData?.sourceClientDifCode || '',
        Category: typesData?.sourceClientCategory || '',
        Subcategory: typesData?.sourceClientSubcategory || '',
        firstname: typesData?.sourceClientFirstname || '',
        lastname: typesData?.sourceClientLastname || '',
        VAT: typesData?.sourceClientVat || typesData?.clientVatNumber || '',
        location: typesData?.sourceClientLocation || typesData?.clientHeadquarters || '',
        email: Array.isArray(typesData?.clientEmails) ? typesData.clientEmails[0] || '' : '',
        phones: Array.isArray(typesData?.contactPhones) ? typesData.contactPhones : Array.isArray(typesData?.sourceClientPhones) ? typesData.sourceClientPhones : [],
        createdAt: typesData?.sourceClientCreatedAt || ''
    };
};

export const read_client_search_results = (clients = [], searchValue = '') => {
    const normalizedSearchValue = read_normalized_key(searchValue);

    if (!normalizedSearchValue.length) {
        return [];
    }

    return clients.filter((clientItem) => {
        const difCode = read_client_dif_code(clientItem);
        const difCodeKey = read_normalized_key(difCode);

        return clientItem?.client === true
            && /^DIF-\d{4,}$/i.test(difCode)
            && difCodeKey === normalizedSearchValue;
    }).slice(0, 8);
};

const read_types_data_reset_key = (typesData = {}, requestOfferMode = false) => {
    const initialRequestClient = read_initial_request_client(typesData);

    return [
        requestOfferMode ? 'request_offer' : 'standard_offer',
        get_initial_types(typesData, requestOfferMode).join(','),
        get_initial_offer_code(typesData),
        get_initial_invoice_enabled(typesData) ? 'invoice' : 'no_invoice',
        get_initial_client_vat_number(typesData),
        get_initial_emails(typesData).join(','),
        get_initial_category(typesData),
        get_initial_subcategory(typesData),
        get_initial_client_name(typesData),
        get_initial_client_headquarters(typesData),
        get_initial_offer_language(typesData),
        read_client_dif_code(initialRequestClient),
        read_client_brand_name(initialRequestClient),
        read_client_category(initialRequestClient),
        read_client_subcategory(initialRequestClient)
    ].join('|');
};

export default function New_Offer_Types(props) {
    const { t } = useTranslation();
    const persistedRequestOfferMode = Boolean(props.requestOfferMode);
    const lockRequestOfferFields = Boolean(props.lockRequestOfferFields);
    const lockOfferType = lockRequestOfferFields || Boolean(props.lockOfferType);
    const typesDataResetKey = read_types_data_reset_key(props.typesData, persistedRequestOfferMode);
    const lastTypesDataResetKey = useRef(typesDataResetKey);
    const [selectedTypes, setSelectedTypes] = useState(get_initial_types(
        props.typesData,
        persistedRequestOfferMode
    ));
    const [invoiceEnabled, setInvoiceEnabled] = useState(get_initial_invoice_enabled(props.typesData));
    const [clientVatNumberValue, setClientVatNumberValue] = useState(get_initial_client_vat_number(props.typesData));
    const [emailValue, setEmailValue] = useState('');
    const [emailList, setEmailList] = useState(get_initial_emails(props.typesData));
    const [emailErrors, setEmailErrors] = useState([]);
    const [industryValue, setIndustryValue] = useState(get_initial_category(props.typesData));
    const [subcategoryValue, setSubcategoryValue] = useState(get_initial_subcategory(props.typesData));
    const [clientNameValue, setClientNameValue] = useState(get_initial_client_name(props.typesData));
    const [clientHeadquartersValue, setClientHeadquartersValue] = useState(get_initial_client_headquarters(props.typesData));
    const [offerLanguageValue, setOfferLanguageValue] = useState(get_initial_offer_language(props.typesData));
    const [exportedClients, setExportedClients] = useState([]);
    const [clientsAreLoading, setClientsAreLoading] = useState(false);
    const [clientsError, setClientsError] = useState('');
    const [clientSearchValue, setClientSearchValue] = useState(read_client_dif_code(read_initial_request_client(props.typesData)));
    const [requestClientErrors, setRequestClientErrors] = useState([]);
    const [selectedRequestClient, setSelectedRequestClient] = useState(read_initial_request_client(props.typesData));
    const [clientContextLoading, setClientContextLoading] = useState(false);
    const [clientContextError, setClientContextError] = useState('');
    const [clientContextLoaded, setClientContextLoaded] = useState(false);
    const clientContextSequence = useRef(0);
    const isRequestOfferMode = persistedRequestOfferMode;
    const formattedEmailValue = emailValue.trim().toLowerCase();
    const emailHasValidFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formattedEmailValue);
    const emailHelpers = [
        !emailHasValidFormat ? t('Use a valid email format.') : null
    ].filter(Boolean);
    const emailSuccesses = [
        emailHasValidFormat ? t('Use a valid email format.') : null
    ].filter(Boolean);
    const clientSearchResults = read_client_search_results(exportedClients, clientSearchValue);
    const requestClientIsSelected = Boolean(selectedRequestClient && read_client_dif_code(selectedRequestClient));
    const visibleTypeNames = selectable_type_names;
    const lockedNifflerPrefill = read_prefill_source_is_niffler(props.typesData);
    const lockedClientPrefill = lockedNifflerPrefill || lockRequestOfferFields;
    const clientSelectionRequired = offer_requires_existing_client({
        requestOfferMode: isRequestOfferMode,
        selectedTypes,
        nifflerPrefill: lockedNifflerPrefill
    });
    const lockedRequestCategory = read_client_category(selectedRequestClient || {}) || industryValue;
    const lockedRequestSubcategory = read_client_subcategory(selectedRequestClient || {}) || subcategoryValue;
    const lockedRequestLocation = read_client_location(selectedRequestClient || {}) || clientHeadquartersValue;
    const lockedRequestVat = read_client_vat(selectedRequestClient || {}) || clientVatNumberValue;
    const lockedRequestName = read_client_brand_name(selectedRequestClient || {}) || clientNameValue;
    const lockedRequestEmails = read_client_emails(selectedRequestClient || {});
    const selectedRequestClientMissingFields = requestClientIsSelected
        ? request_client_missing_fields(selectedRequestClient || {})
        : [];
    const lockedOfferCode = read_locked_text(props.typesData?.offerCode, t('Assigned automatically'));
    const canGoNext = selectedTypes.length > 0
        && !clientContextLoading && !clientContextError
        && (!props.enforceClientEligibility || clientContextLoaded)
        && (
            clientSelectionRequired
                ? requestClientIsSelected && (!isRequestOfferMode || !selectedRequestClientMissingFields.length)
                : emailList.length > 0
        );

    useEffect(() => {
        if (lastTypesDataResetKey.current === typesDataResetKey) {
            return;
        }

        lastTypesDataResetKey.current = typesDataResetKey;
        setSelectedTypes(get_initial_types(
            props.typesData,
            persistedRequestOfferMode
        ));
        setInvoiceEnabled(get_initial_invoice_enabled(props.typesData));
        setClientVatNumberValue(get_initial_client_vat_number(props.typesData));
        setEmailList(get_initial_emails(props.typesData));
        setEmailErrors([]);
        setIndustryValue(get_initial_category(props.typesData));
        setSubcategoryValue(get_initial_subcategory(props.typesData));
        setClientNameValue(get_initial_client_name(props.typesData));
        setClientHeadquartersValue(get_initial_client_headquarters(props.typesData));
        setOfferLanguageValue(get_initial_offer_language(props.typesData));
        setSelectedRequestClient(read_initial_request_client(props.typesData));
        setClientSearchValue(read_client_dif_code(read_initial_request_client(props.typesData)));
        setRequestClientErrors([]);
    }, [props.typesData, persistedRequestOfferMode, typesDataResetKey]);

    useEffect(() => {
        const normalizedClientSearchValue = clientSearchValue.trim();

        if (!clientSelectionRequired || requestClientIsSelected || !normalizedClientSearchValue.length) {
            setExportedClients([]);
            setClientsAreLoading(false);
            setClientsError('');
            return;
        }

        let mounted = true;
        const searchTimer = window.setTimeout(() => {
            search_clients(normalizedClientSearchValue)
                .then((clients) => {
                    if (!mounted) {
                        return;
                    }

                    setExportedClients(clients);
                })
                .catch(() => {
                    if (!mounted) {
                        return;
                    }

                    setClientsError(t('Could not search clients.'));
                })
                .finally(() => {
                    if (!mounted) {
                        return;
                    }

                    setClientsAreLoading(false);
                });
        }, 250);

        setExportedClients([]);
        setClientsAreLoading(true);
        setClientsError('');

        return () => {
            mounted = false;
            window.clearTimeout(searchTimer);
        };
    }, [clientSearchValue, clientSelectionRequired, requestClientIsSelected, t]);

    const select_type = (typeName, checked) => {
        if (lockOfferType) {
            return;
        }

        if (!offer_type_is_available(typeName)) {
            return;
        }

        if (checked) {
            if (!isRequestOfferMode && request_client_should_reset_on_type_change(selectedTypes, typeName)) {
                setSelectedRequestClient(null);
                setClientSearchValue('');
                setRequestClientErrors([]);
                setExportedClients([]);
                setClientNameValue(get_initial_client_name(props.typesData));
                setClientHeadquartersValue(get_initial_client_headquarters(props.typesData));
                setClientVatNumberValue(get_initial_client_vat_number(props.typesData));
                setIndustryValue(get_initial_category(props.typesData));
                setSubcategoryValue(get_initial_subcategory(props.typesData));
                setEmailList(get_initial_emails(props.typesData));
            }

            setSelectedTypes([typeName]);
            return;
        }

        if (!isRequestOfferMode && typeName === 'marketing') {
            setSelectedRequestClient(null);
            setClientSearchValue('');
            setRequestClientErrors([]);
            setExportedClients([]);
            setClientNameValue(get_initial_client_name(props.typesData));
            setClientHeadquartersValue(get_initial_client_headquarters(props.typesData));
            setClientVatNumberValue(get_initial_client_vat_number(props.typesData));
            setIndustryValue(get_initial_category(props.typesData));
            setSubcategoryValue(get_initial_subcategory(props.typesData));
            setEmailList(get_initial_emails(props.typesData));
        }

        setSelectedTypes(selectedTypes.filter((item) => item !== typeName));
    };

    const change_email = (event) => {
        setEmailValue(event.target.value);
        setEmailErrors([]);
    };

    const change_client_vat_number = (event) => {
        setClientVatNumberValue(event.target.value);
    };

    const toggle_invoice = () => {
        const nextInvoiceEnabled = !invoiceEnabled;

        setInvoiceEnabled(nextInvoiceEnabled);
    };

    const change_industry = (event) => {
        setIndustryValue(event.target.value);
    };

    const change_client_name = (event) => {
        setClientNameValue(event.target.value);
    };

    const change_client_headquarters = (event) => {
        setClientHeadquartersValue(event.target.value);
    };

    const change_offer_language = (event) => {
        setOfferLanguageValue(event.target.value);
    };

    const change_client_search = (event) => {
        clientContextSequence.current += 1;
        setClientContextLoading(false);
        setClientContextLoaded(false);
        setClientContextError('');
        setClientSearchValue(event.target.value);
        setSelectedRequestClient(null);
        setRequestClientErrors([]);

        if (isRequestOfferMode) {
            setClientNameValue('');
            setClientHeadquartersValue('');
            setClientVatNumberValue('');
            setIndustryValue('');
            setSubcategoryValue('');
            setEmailList([]);
        }
    };

    const add_email = () => {
        const nextEmail = emailValue.trim().toLowerCase();

        if (!nextEmail.length) {
            setEmailErrors([t('Please enter an email before adding it.')]);
            return;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail)) {
            setEmailErrors([t('Please enter a valid email address.')]);
            return;
        }

        if (emailList.includes(nextEmail)) {
            setEmailErrors([t('This email is already in the list.')]);
            return;
        }

        setEmailList([...emailList, nextEmail]);
        setEmailValue('');
        setEmailErrors([]);
    };

    const remove_email = (index) => {
        setEmailList(emailList.filter((_, emailIndex) => emailIndex !== index));
    };

    const request_client_error_messages = (missingFields = []) => {
        if (!missingFields.length) {
            return [];
        }

        return [`${t('Client card is missing required fields')}: ${missingFields.map((fieldName) => t(fieldName)).join(', ')}.`];
    };

    const apply_client_context = (client) => {
        setSelectedRequestClient(client);
        setClientSearchValue(read_client_dif_code(client));
        setClientNameValue(read_client_brand_name(client));
        setClientHeadquartersValue(read_client_location(client));
        setClientVatNumberValue(read_client_vat(client));
        setIndustryValue(read_client_category(client));
        setSubcategoryValue(read_client_subcategory(client));
        setEmailList(read_client_emails(client));
        setEmailErrors([]);
        setRequestClientErrors([]);
    };

    const refresh_client_context = async(client = selectedRequestClient) => {
        const sequence = ++clientContextSequence.current;
        setClientContextLoading(true);
        setClientContextLoaded(false);
        setClientContextError('');
        try {
            const context = await get_offer_eligibility({
                mode: isRequestOfferMode ? 'request' : 'sale',
                typesData: {
                    ...props.typesData,
                    sourceClient: client || {},
                    sourceClientId: read_client_id(client || {}),
                    sourceClientDifCode: read_client_dif_code(client || {})
                }
            });
            if (sequence !== clientContextSequence.current) return null;
            if (context.client) apply_client_context(context.client);
            let restriction = context.restriction || '';
            if (context.restrictionCode === 'INCOMPLETE_CLIENT') {
                restriction = t('Complete these fields in Leads or Clients, then refresh: {{fields}}.', { fields: context.missingFields.map(field => t(field)).join(', ') });
            }
            setClientContextError(restriction);
            setClientContextLoaded(true);
            return context.allowed ? context : null;
        } catch (error) {
            if (sequence === clientContextSequence.current) {
                setClientContextError(error?.response?.data?.message || t('Could not verify the client or workforce. Please retry.'));
            }
            return null;
        } finally {
            if (sequence === clientContextSequence.current) setClientContextLoading(false);
        }
    };

    useEffect(() => {
        if (props.enforceClientEligibility && (read_initial_request_client(props.typesData) || (!isRequestOfferMode && props.typesData?.sourceBrandId))) {
            refresh_client_context(read_initial_request_client(props.typesData));
        }
        return () => { clientContextSequence.current += 1; };
    }, [props.typesData?.sourceBrandId, props.typesData?.sourceClientId, props.typesData?.sourceClientDifCode, props.enforceClientEligibility, isRequestOfferMode]);

    const select_request_client = (client) => {
        apply_client_context(client);
        if (props.enforceClientEligibility) refresh_client_context(client);
    };

    const go_to_next = async() => {
        if (clientContextLoading) return;
        let verifiedClient = selectedRequestClient;
        let verifiedContext = null;
        if (props.enforceClientEligibility) {
            const context = await refresh_client_context();
            if (!context) return;
            verifiedContext = context;
            verifiedClient = context.client || selectedRequestClient;
        }
        if (clientSelectionRequired && !requestClientIsSelected) {
            setRequestClientErrors([t('Enter and select an existing client DIF code.')]);
            return;
        }

        if (isRequestOfferMode && selectedRequestClientMissingFields.length) {
            setRequestClientErrors(request_client_error_messages(selectedRequestClientMissingFields));
            return;
        }

        const requestClientEmails = read_client_emails(verifiedClient || {});
        const requestClientPhones = read_client_phones(verifiedClient || {});
        const requestClientCategory = read_client_category(verifiedClient || {});
        const requestClientSubcategory = read_client_subcategory(verifiedClient || {});
        const requestClientName = read_client_brand_name(verifiedClient || {});
        const requestClientVat = read_client_vat(verifiedClient || {});
        const requestClientDifCode = read_client_dif_code(verifiedClient || {});
        const requestClientId = read_client_id(verifiedClient || {});
        const requestClientContactName = read_client_contact_name(verifiedClient || {});
        const requestClientLocation = read_client_location(verifiedClient || {});
        const useRequestClientValues = Boolean(verifiedClient) || clientSelectionRequired || lockRequestOfferFields;
        const clientReporter = verifiedContext?.reporter || props.typesData?.clientReporter || null;

        if (props.onNext) {
            props.onNext({
                selectedTypes,
                clientEmails: useRequestClientValues ? (requestClientEmails.length ? requestClientEmails : emailList) : emailList,
                contactPhone: useRequestClientValues ? (requestClientPhones[0] || '') : '',
                contactPhones: useRequestClientValues ? requestClientPhones : [],
                category: useRequestClientValues ? (requestClientCategory || industryValue).trim().toLowerCase() : industryValue.trim().toLowerCase(),
                subcategory: useRequestClientValues ? (requestClientSubcategory || subcategoryValue).trim().toLowerCase() : subcategoryValue.trim().toLowerCase(),
                industry: useRequestClientValues ? (requestClientCategory || industryValue).trim().toLowerCase() : industryValue.trim().toLowerCase(),
                offerCode: props.typesData?.offerCode || '',
                clientName: useRequestClientValues ? (requestClientName || clientNameValue.trim()) : clientNameValue.trim(),
                clientHeadquarters: useRequestClientValues ? (requestClientLocation || clientHeadquartersValue.trim()) : clientHeadquartersValue.trim(),
                clientVatNumber: useRequestClientValues ? (requestClientVat || clientVatNumberValue.trim()) : (invoiceEnabled ? clientVatNumberValue.trim() : ''),
                offerLanguage: offerLanguageValue,
                invoiceEnabled,
                requestOfferMode: isRequestOfferMode,
                sourceSystem: props.typesData?.sourceSystem || (isRequestOfferMode ? 'request_offer' : ''),
                preofferEnabled: get_preoffer_enabled_value(props.typesData),
                preofferAssignedTeamLead: props.typesData?.preofferAssignedTeamLead || null,
                sourceClient: useRequestClientValues ? verifiedClient : null,
                sourceClientId: useRequestClientValues ? requestClientId : '',
                sourceClientDifCode: useRequestClientValues ? requestClientDifCode : '',
                sourceClientBrandName: useRequestClientValues ? (requestClientName || clientNameValue.trim()) : '',
                sourceClientCategory: useRequestClientValues ? (requestClientCategory || industryValue) : '',
                sourceClientSubcategory: useRequestClientValues ? (requestClientSubcategory || subcategoryValue) : '',
                sourceClientFirstname: useRequestClientValues ? read_normalized_text(verifiedClient?.firstName || verifiedClient?.firstname) : '',
                sourceClientLastname: useRequestClientValues ? read_normalized_text(verifiedClient?.lastName || verifiedClient?.lastname) : '',
                sourceClientContactName: useRequestClientValues ? requestClientContactName : '',
                sourceClientPhones: useRequestClientValues ? requestClientPhones : [],
                sourceClientLocation: useRequestClientValues ? (requestClientLocation || clientHeadquartersValue.trim()) : '',
                sourceClientVat: useRequestClientValues ? (requestClientVat || clientVatNumberValue.trim()) : '',
                sourceClientCreatedAt: useRequestClientValues ? read_normalized_text(verifiedClient?.createdAt) : '',
                clientReporter,
                reporterReviewerRequired: false
            });
        }
    };

    return (
        <NewOfferTypes>
            <FormBox>
                <FormBoxTitle>
                    <h4>{t('Select one type to create')}</h4>
                </FormBoxTitle>
                {lockOfferType ? (
                    <NewOfferLockedSelection>
                        <p>{read_locked_list_text(selectedTypes.map((typeName) => read_type_title(typeName)), t('No type selected'))}</p>
                    </NewOfferLockedSelection>
                ) : (
                    <NewOfferTypesGrid>
                        {visibleTypeNames.map((typeName) => {
                            const typeIsEnabled = offer_type_is_available(typeName);
                            const typeUnavailableText = !typeIsEnabled ? t('Unavailable for new offers') : null;

                            return (
                                <Checkbox_Extra
                                    key={typeName}
                                    title={read_type_title(typeName)}
                                    text={typeUnavailableText}
                                    checked={selectedTypes.includes(typeName)}
                                    disabled={!typeIsEnabled}
                                    onChange={(checked) => select_type(typeName, checked)}
                                />
                            );
                        })}
                    </NewOfferTypesGrid>
                )}
            </FormBox>
            {!isRequestOfferMode ? (
                <FormBox>
                    <FormBoxTitle>
                        <h4>{t('Offer code')}</h4>
                    </FormBoxTitle>
                    <NewOfferLockedSelection>
                        <p>{lockedOfferCode}</p>
                        {!props.typesData?.offerCode && props.typesData?.draftOfferId ? (
                            <h6>{read_locked_text(props.typesData?.draftOfferId, t('Draft offer'))}</h6>
                        ) : null}
                    </NewOfferLockedSelection>
                </FormBox>
            ) : null}
            {clientSelectionRequired ? (
                <>
                    <FormBox>
                        <FormBoxTitle>
                            <h4>{isRequestOfferMode ? t('Search client by DIF code') : t('Personalised ads client DIF code')}</h4>
                        </FormBoxTitle>
                        <Builder_>
                            <Input_
                                placeholder={t('DIF code')}
                                value={clientSearchValue}
                                onChange={change_client_search}
                                errors={requestClientErrors}
                            />
                        </Builder_>
                        {clientsAreLoading ? <p>{t('Loading clients...')}</p> : null}
                        {clientsError ? <p>{clientsError}</p> : null}
                        {clientSearchValue.length && !requestClientIsSelected ? (
                            <RequestClientResults>
                                {clientSearchResults.map((clientItem) => (
                                    <RequestClientOption
                                        key={`${read_client_dif_code(clientItem)}_${read_client_brand_name(clientItem)}`}
                                        type="button"
                                        onClick={() => select_request_client(clientItem)}
                                    >
                                        <h6>{read_client_dif_code(clientItem)}</h6>
                                        <p>{read_client_brand_name(clientItem)}</p>
                                        <p>{read_client_industry(clientItem)}</p>
                                    </RequestClientOption>
                                ))}
                                {!clientSearchResults.length && !clientsAreLoading && !clientsError ? <p>{t('No client found')}</p> : null}
                            </RequestClientResults>
                        ) : null}
                    </FormBox>
                    {requestClientIsSelected ? (
                        <FormBox>
                            <FormBoxTitle>
                                <h4>{t('Client Details')}</h4>
                            </FormBoxTitle>
                            <RequestClientLockedDetails>
                                <RequestClientLockedRow>
                                    <h6>{t('DIF code')}</h6>
                                    <p>{read_client_dif_code(selectedRequestClient)}</p>
                                </RequestClientLockedRow>
                                <RequestClientLockedRow>
                                    <h6>{t('Brand')}</h6>
                                    <p>{read_client_brand_name(selectedRequestClient)}</p>
                                </RequestClientLockedRow>
                                <RequestClientLockedRow>
                                    <h6>{t('Category')}</h6>
                                    <p>{read_client_category(selectedRequestClient) || '-'}</p>
                                </RequestClientLockedRow>
                                <RequestClientLockedRow>
                                    <h6>{t('Subcategory')}</h6>
                                    <p>{read_client_subcategory(selectedRequestClient) || '-'}</p>
                                </RequestClientLockedRow>
                                <RequestClientLockedRow>
                                    <h6>{t('Phones')}</h6>
                                    <p>{read_locked_list_text(read_client_phones(selectedRequestClient), '-')}</p>
                                </RequestClientLockedRow>
                                <RequestClientLockedRow>
                                    <h6>{t('Emails')}</h6>
                                    <p>{read_locked_list_text(read_client_emails(selectedRequestClient), '-')}</p>
                                </RequestClientLockedRow>
                                <RequestClientLockedRow>
                                    <h6>{t('VAT number')}</h6>
                                    <p>{read_client_vat(selectedRequestClient) || '-'}</p>
                                </RequestClientLockedRow>
                                <RequestClientLockedRow>
                                    <h6>{t('Location')}</h6>
                                    <p>{read_client_location(selectedRequestClient) || '-'}</p>
                                </RequestClientLockedRow>
                                <RequestClientLockedRow>
                                    <h6>{t('Contact person')}</h6>
                                    <p>{read_client_contact_name(selectedRequestClient) || '-'}</p>
                                </RequestClientLockedRow>
                            </RequestClientLockedDetails>
                        </FormBox>
                    ) : null}
                </>
            ) : lockedClientPrefill ? (
                <FormBox>
                    <FormBoxTitle>
                        <h4>{t('Client Details')}</h4>
                    </FormBoxTitle>
                    {lockRequestOfferFields ? (
                        <Builder_>
                            <FormBox>
                                <FormBoxTitle>
                                    <h4>{t('DIF code')}</h4>
                                </FormBoxTitle>
                                <NewOfferLockedSelection>
                                    <p>{read_locked_text(read_client_dif_code(selectedRequestClient || {}), t('No DIF code'))}</p>
                                </NewOfferLockedSelection>
                            </FormBox>
                        </Builder_>
                    ) : null}
                    <Builder_>
                        <FormBox>
                            <FormBoxTitle>
                                <h4>{t('Category')}</h4>
                            </FormBoxTitle>
                            <NewOfferLockedSelection>
                                <p>{read_locked_text(lockRequestOfferFields ? lockedRequestCategory : industryValue, t('No category'))}</p>
                            </NewOfferLockedSelection>
                        </FormBox>
                    </Builder_>
                    <Builder_>
                        <FormBox>
                            <FormBoxTitle>
                                <h4>{t('Subcategory')}</h4>
                            </FormBoxTitle>
                            <NewOfferLockedSelection>
                                <p>{read_locked_text(lockRequestOfferFields ? lockedRequestSubcategory : subcategoryValue, t('No subcategory'))}</p>
                            </NewOfferLockedSelection>
                        </FormBox>
                    </Builder_>
                    <Builder_>
                        <FormBox>
                            <FormBoxTitle>
                                <h4>{t('Brand or full name')}</h4>
                            </FormBoxTitle>
                            <NewOfferLockedSelection>
                                <p>{read_locked_text(lockRequestOfferFields ? lockedRequestName : clientNameValue, t('No client name'))}</p>
                            </NewOfferLockedSelection>
                        </FormBox>
                    </Builder_>
                    <Builder_>
                        <FormBox>
                            <FormBoxTitle>
                                <h4>{t('Headquarters')}</h4>
                            </FormBoxTitle>
                            <NewOfferLockedSelection>
                                <p>{read_locked_text(lockRequestOfferFields ? lockedRequestLocation : clientHeadquartersValue, t('No headquarters'))}</p>
                            </NewOfferLockedSelection>
                        </FormBox>
                    </Builder_>
                    <Builder_>
                        <FormBox>
                            <FormBoxTitle>
                                <h4>{t('Offer language')}</h4>
                            </FormBoxTitle>
                            <Dropdown_>
                                <select value={offerLanguageValue} onChange={change_offer_language}>
                                    <option value="el">{t('Greek')}</option>
                                    <option value="en">{t('English')}</option>
                                </select>
                            </Dropdown_>
                        </FormBox>
                    </Builder_>
                    <Builder_>
                        <FormBox>
                            <FormBoxTitle>
                                <h4>{t('Email')}</h4>
                            </FormBoxTitle>
                            <NewOfferLockedSelection>
                                <p>{read_locked_list_text(lockRequestOfferFields && lockedRequestEmails.length ? lockedRequestEmails : emailList, t('No email'))}</p>
                            </NewOfferLockedSelection>
                        </FormBox>
                    </Builder_>
                    <Builder_>
                        <FormBox>
                            <FormBoxTitle>
                                <h4>{t('VAT number')}</h4>
                            </FormBoxTitle>
                            <NewOfferLockedSelection>
                                <p>{read_locked_text(lockRequestOfferFields ? lockedRequestVat : clientVatNumberValue, t('No VAT number'))}</p>
                            </NewOfferLockedSelection>
                        </FormBox>
                    </Builder_>
                </FormBox>
            ) : (
                <FormBox>
                    <FormBoxTitle>
                        <h4>{t('Client Details')}</h4>
                    </FormBoxTitle>
                    <Builder_>
                        <Input_
                            placeholder={t('Category')}
                            value={industryValue}
                            onChange={change_industry}
                        />
                    </Builder_>
                    <Builder_>
                        <Input_
                            placeholder={t('Brand or full name')}
                            Required
                            value={clientNameValue}
                            onChange={change_client_name}
                        />
                    </Builder_>
                    <Builder_>
                        <Input_
                            placeholder={t('Headquarters')}
                            value={clientHeadquartersValue}
                            onChange={change_client_headquarters}
                        />
                    </Builder_>
                    <Builder_>
                        <Dropdown_>
                            <select value={offerLanguageValue} onChange={change_offer_language}>
                                <option value="el">{t('Greek')}</option>
                                <option value="en">{t('English')}</option>
                            </select>
                        </Dropdown_>
                    </Builder_>
                    <Builder_>
                        <Input_
                            placeholder={t('Email')}
                            type="email"
                            value={emailValue}
                            onChange={change_email}
                            errors={emailErrors}
                            helpers={emailErrors.length ? [] : emailHelpers}
                            successes={emailErrors.length ? [] : emailSuccesses}
                            list={emailList}
                            add={t('Add')}
                            onAdd={add_email}
                            onDelete={remove_email}
                            icon_ListItem="Email"
                            required = {!emailList.length}
                        />
                    </Builder_>
                    <Builder_>
                        <NewOfferTypesToggle>
                            <h6>{t('Invoice')}</h6>
                            <Toggle_
                                active={invoiceEnabled}
                                onToggle={toggle_invoice}
                            />
                        </NewOfferTypesToggle>
                    </Builder_>
                    {invoiceEnabled ? (
                        <Builder_>
                            <Input_
                                placeholder={t('VAT number')}
                                value={clientVatNumberValue}
                                onChange={change_client_vat_number}
                            />
                        </Builder_>
                    ) : null}
                </FormBox>
            )}
            {clientContextLoading ? <p role="status">{t('Verifying client...')}</p> : null}
            {clientContextError ? <p role="alert">{clientContextError}</p> : null}
            {props.enforceClientEligibility && (requestClientIsSelected || lockedNifflerPrefill || props.typesData?.sourceBrandId) ? (
                <Main_Plain
                    text={t('Refresh client details')}
                    onClick={() => refresh_client_context()}
                    disabled={clientContextLoading}
                ></Main_Plain>
            ) : null}
            <Actions_>
                <Grey_
                    text={t('Exit')}
                    iconRight="Xicon"
                    onClick={props.exit}
                />
                {canGoNext ? (
                    <Main_
                        text={t('Next')}
                        iconRight="Next"
                        onClick={go_to_next}
                    />
                ) : null}
            </Actions_>
        </NewOfferTypes>
    );
}
